import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IntegrationsIcon from '@mui/icons-material/ExtensionOutlined';

import { crmService } from '../../../services/api';
import { useIntegrations } from '../../../hooks/useIntegrations';
import BoundedPageLayout from '../../../components/dashboard/BoundedPageLayout';
import DataGridCard from '../../../components/dashboard/DataGridCard';
import ListFilterToolbar from '../../../components/dashboard/ListFilterToolbar';
import PageHeader from '../../../components/dashboard/PageHeader';
import LoadingState from '../../../components/dashboard/LoadingState';
import ErrorState from '../../../components/dashboard/ErrorState';
import PermissionDenied from '../../../components/dashboard/PermissionDenied';
import SoftChip from '../../../components/dashboard/SoftChip';
import SuccessSnackbar from '../../../components/dashboard/SuccessSnackbar';
import UndoSnackbar from '../../../components/dashboard/UndoSnackbar';
import useUndoDelete from '../../../components/dashboard/useUndoDelete';
import IntegrationLogo from './IntegrationLogo';
import { STATUSES, integrationStatus, statusMeta } from './integrationStatus';

// Section order on the page; the catalog's `type` values.
const TYPES = ['crm', 'stock'];

function IntegrationCard({ integration, status }) {
  const { t } = useTranslation();
  const { color, action } = statusMeta[status];

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent component={Stack} spacing={2} height="100%">
        <Stack direction="row" spacing={2} alignItems="center">
          <IntegrationLogo integration={integration} />
          <Stack minWidth={0}>
            <Typography variant="subtitle1" fontWeight={600} noWrap>
              {integration.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t(`integrations.types.${integration.type}`)}
            </Typography>
          </Stack>
        </Stack>
        <Typography variant="body2" color="text.secondary" flexGrow={1}>
          {t(`integrations.providers.${integration.key}.description`, { defaultValue: '' })}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <SoftChip size="small" color={color} label={t(`integrations.statuses.${status}`)} />
          <Button
            component={RouterLink}
            to={`/integrations/${integration.type}/${integration.key}${status === 'available' ? '/new' : ''}`}
            variant={action === 'continue' ? 'contained' : 'outlined'}
          >
            {t(`integrations.actions.${action}`)}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function IntegrationsList() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');

  const query = useIntegrations();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [flashMessage, setFlashMessage] = useState('');

  // ids are { type, id }: a config row lives in /crm-configs/ or /stock-configs/.
  const { pending, undo, dismiss, seed } = useUndoDelete({
    onDelete: ({ type: rowType, id }) => crmService.deleteIntegrationConfig(rowType, id),
    onRestore: ({ type: rowType, id }) =>
      crmService
        .restoreIntegrationConfig(rowType, id)
        .then(() => queryClient.invalidateQueries({ queryKey: ['integrations'] })),
  });

  // Delete comes from the detail page, create from the form: both land here.
  useEffect(() => {
    const seedDelete = location.state?.seedDelete;
    if (seedDelete) {
      seed(seedDelete.ids, seedDelete.message);
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    if (location.state?.flashMessage) {
      setFlashMessage(location.state.flashMessage);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const header = (
    <PageHeader
      icon={<IntegrationsIcon color="primary" />}
      title={t('integrations.title')}
      breadcrumbs={[
        { label: t('integrations.breadcrumb.dashboard') },
        { label: t('nav.groups.settings') },
        { label: t('integrations.breadcrumb.list') },
      ]}
    />
  );

  const renderBody = () => {
    if (query.isLoading) return <LoadingState message={t('integrations.states.loading')} />;
    if (query.isError) {
      if (query.error?.response?.status === 403) {
        return (
          <PermissionDenied
            title={t('integrations.states.permissionDeniedTitle')}
            message={t('integrations.states.permissionDeniedMessage')}
          />
        );
      }
      return (
        <ErrorState
          title={t('integrations.states.errorTitle')}
          message={t('integrations.states.errorMessage')}
          actionLabel={t('integrations.states.retry')}
          onRetry={query.refetch}
        />
      );
    }

    const term = search.trim().toLocaleLowerCase();
    const visible = query.data
      .map((integration) => ({ integration, status: integrationStatus(integration) }))
      .filter(({ integration, status: itemStatus }) =>
        (!type || integration.type === type)
        && (!status || itemStatus === status)
        && (!term || integration.name.toLocaleLowerCase().includes(term)));
    const sections = TYPES
      .map((sectionType) => ({
        type: sectionType,
        items: visible.filter(({ integration }) => integration.type === sectionType),
      }))
      .filter((section) => section.items.length > 0);

    const chips = [];
    if (type) {
      chips.push({
        key: 'type',
        label: `${t('integrations.filters.type')}: ${t(`integrations.sections.${type}.title`)}`,
        onDelete: () => setType(''),
      });
    }
    if (status) {
      chips.push({
        key: 'status',
        label: `${t('integrations.filters.status')}: ${t(`integrations.statuses.${status}`)}`,
        onDelete: () => setStatus(''),
      });
    }
    if (search) {
      chips.push({
        key: 'search',
        label: `${t('integrations.filters.searchChip')}: ${search}`,
        onDelete: () => setSearch(''),
      });
    }

    return (
      <Stack spacing={3} width="100%" pb={3}>
        <DataGridCard sx={{ flex: '0 0 auto' }}>
          <ListFilterToolbar
            showGridControls={false}
            filters={[
              {
                field: 'type',
                label: t('integrations.filters.type'),
                value: type,
                onChange: setType,
                options: TYPES.map((value) => ({ value, label: t(`integrations.sections.${value}.title`) })),
              },
              {
                field: 'status',
                label: t('integrations.filters.status'),
                value: status,
                onChange: setStatus,
                options: STATUSES.map((value) => ({ value, label: t(`integrations.statuses.${value}`) })),
              },
            ]}
            search={{ label: t('integrations.filters.search'), value: search, onChange: setSearch }}
            chips={chips}
            resultsLabel={t('integrations.filters.resultsFound', { count: visible.length })}
            clearLabel={t('integrations.filters.clear')}
            onClearAll={() => {
              setSearch('');
              setType('');
              setStatus('');
            }}
          />
        </DataGridCard>

        {sections.length === 0 && (
          <Typography color="text.secondary" textAlign="center" py={6}>
            {t('integrations.states.empty')}
          </Typography>
        )}

        {sections.map((section) => (
          <Stack key={section.type} spacing={2} component="section">
            <Stack direction="row" spacing={1.5} alignItems="baseline" useFlexGap flexWrap="wrap">
              <Typography variant="h6" component="h2">
                {t(`integrations.sections.${section.type}.title`)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(`integrations.sections.${section.type}.description`)}
              </Typography>
            </Stack>
            <Grid container spacing={2}>
              {section.items.map(({ integration, status: itemStatus }) => (
                <Grid key={integration.key} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <IntegrationCard integration={integration} status={itemStatus} />
                </Grid>
              ))}
            </Grid>
          </Stack>
        ))}
      </Stack>
    );
  };

  return (
    <BoundedPageLayout header={header}>
      {renderBody()}
      <SuccessSnackbar open={Boolean(flashMessage)} message={flashMessage} onClose={() => setFlashMessage('')} />
      <UndoSnackbar pending={pending} onUndo={undo} onDismiss={dismiss} undoLabel={t('integrations.deleteUndo')} />
    </BoundedPageLayout>
  );
}
